import { isEmail } from '@atlaskit/user-picker';

import type {
	FlagDescriptor,
	ShowFlagArgs,
} from '@confluence/flags/entry-points/FlagsStateContainer';
import type { AccessRequestsTableProps } from '@confluence/access-requests/entry-points/AccessRequestsTable';
import { fg } from '@confluence/feature-gating';

import type {
	UserPickerPrincipal,
	Role,
	StagedPrincipalsAddMode,
	UserPrincipal,
	Principal,
} from '../controlled/commonTypes';
import { validateRestrictedPrincipals } from '../controlled/resolveRestrictions/validateRestrictedPrincipals';

import type { RefetchQueries } from './queries/useQueries';
import { type ControllerExtras, type ControllerProps, type QueriesState } from './Controller';
import { type MasterStateSetter, type MasterState } from './masterState';
import type { DerivedState } from './derivedState';
import type { CtaFlowEventDispatcher } from './ctaFlowController';
import {
	stagePrincipalReassignment,
	stagePrincipalRemoval,
	convertUserPickerPrincipal,
	stagePrincipalAddition,
	filterStagedAdditionsByPrincipals,
} from './principals';
import { makePrincipalsMap } from './principalsMap';

export type Actions = {
	/**
	 * User tried to dismiss the main dialog but has not had a chance to
	 * reconsider if they have changes staged
	 */
	dismiss: () => void;

	/** User reconsidered closing without saving */
	dismissCloseConfirmationDialog: () => void;

	/**
	 * A dialog that was potentially the last in the workflow just completed its
	 * exit animation. If the teardown controller is primed to end the session
	 * after such animations complete, it should do so now.
	 */
	potentialFinalDialogDidCompleteClose: () => void;

	/** User confirmed closing without saving */
	closeWithoutSaving: () => void;

	/**
	 * User staged a change to the open/restricted aspect of the General Access
	 * control
	 */
	changeGeneralAccessOpenRestricted: (becomeRestricted: boolean) => void;

	/**
	 * User staged a change to the can-view/can-edit aspect of the open access
	 * state for the General Access control
	 */
	changeGeneralAccessCanViewCanEdit: (becomeCanEdit: boolean) => void;

	/** Change the staged principals list */
	changeStagedPrincipals: (principalsStagerPrincipals: UserPickerPrincipal[]) => void;

	/** Change the staged setting for what to do with staged principals */
	changeAddMode: (
		addMode: StagedPrincipalsAddMode,
		principalsStagerPrincipals: UserPickerPrincipal[],
	) => void;

	/** Change the role a principal in the specific access list should have */
	changeSpecificAccessRole: (role: Role, id: string) => void;

	/** Remove a principal from the Specific Access list */
	removeSpecificAccessPrincipal: (id: string) => void;

	/** User wants all specific access principals removed */
	removeAllSpecificAccessPrincipals: () => void;

	/** User tried to dismiss the Space Access Details dialog */
	dismissSpaceAccessDetails: () => void;

	/** User opens Space Access Details dialog */
	openSpaceAccessDetails: () => void;

	/** User opens Open Access Warning dialog */
	showOpenAccessWarningDialog: () => void;

	/** User closes Open Access Warning dialog */
	closeOpenAccessWarningDialog: () => void;

	/** User tried to dismiss the Inspect Perms dialog */
	openInspectPermsDialog: () => void;

	/** User tried to dismiss the Inspect Perms dialog */
	dismissInspectPermsDialog: () => void;

	/**
	 * User clicks the Public link button in the Distribution Footer
	 */
	openPublicLinkDialog: () => void;

	/**
	 * User clicks the back button in the Public Link dialog
	 */
	closePublicLinkDialog: () => void;

	/**
	 * User clicks the Slack button in the Distribution Footer
	 */
	openShareToSlackDialog: () => void;

	/**
	 * User clcks on the Access Requests button in the Header
	 */
	openAccessRequestsDialog: () => void;

	/**
	 * User clcks on the Access Requests button in the Header
	 */
	closeAccessRequestsDialog: () => void;

	/**
	 * User clicks the back button in the Share to Slack dialog
	 */
	closeShareToSlackDialog: () => void;

	/**
	 * Clear the focus element if it is the last triggered dialog variant
	 */
	clearElementToFocus: () => void;

	/**
	 * Generic action to show any flag
	 */
	showFlag: (flag: Omit<FlagDescriptor, 'id'>) => Promise<FlagDescriptor>;

	/**
	 * Generic action to show error flag
	 */
	showErrorFlag: (flag: ShowFlagArgs) => Promise<FlagDescriptor>;

	/** User clicked the CTA, whatever that might have said at the time */
	handleCtaClick: () => void;

	/** User staged a change to the optional message */
	changeOptionalMessage: (message: string) => void;

	/** Set the state of the should-notify checkbox */
	setNotifyChecked: (newValue: boolean) => void;

	/**
	 * Toggle whether to show the message composer under the notify-new-people
	 * checkbox
	 */
	toggleNotifyAddMessage: () => void;

	/** User confirms that they want to include groups in notifications */
	doNotifyGroupsConfirmed: () => void;

	/** User confirms that they do not want to include groups in notifications */
	doNotNotifyGroupsConfirmed: () => void;

	/** User confirms that they want to ignore access restrictions and continue */
	ignoreRestrictionsAndContinue: (isGuestError?: boolean) => void;

	/** Restrictions are fixed or skipped  */
	resolveRestrictionsComplete: () => void;

	/** Toggle whether to show the Specific Access list */
	toggleSpecificAccessExpanded: () => void;

	/** Used by Access Requests Dialog to try and resolve blocked access for a user (one at a time) */
	maybeStartBlockedAccessFlowForUser: AccessRequestsTableProps['maybeStartBlockedAccessFlowForUser'];

	/** Closes the Resolve Restrictions overlay dialog for Access Requests */
	closeAccessRequestsResolveRestrictionsDialog: () => void;

	/** Closes the Warn Restrictions overlay dialog for Access Requests */
	closeAccessRequestsWarnRestrictionsDialog: () => void;

	/** Set whether there are unsaved changes in the Access Requests dialog */
	setAccessRequestsChanges: (hasChanges: boolean) => void;

	/** Set status of skipped principals on User Picker option */
	markPrincipalsAsSkipped: (skippedPrincipals: string[]) => void;

	/** Set status of invited principals on User Picker option */
	markPrincipalsAsInvited: (
		invitedPrincipals: { id: string; email: string }[],
		errorPrincipals: { email: string }[],
	) => void;
};

/**
 * Adds principals to the principal map and stages them in the staged addition list.
 * The staged addition list holds principal that will be processed when the CTA is clicked.
 *
 * This callback is currently used for:
 * - Updating the principal stager list
 * - Handling dropdown changes for permissions
 */
function addPrincipals({
	principalsStagerPrincipals,
	addMode,
	queriesState,
	setMasterState,
}: {
	principalsStagerPrincipals: UserPickerPrincipal[];
	addMode?: StagedPrincipalsAddMode;
	queriesState: QueriesState;
	setMasterState: MasterStateSetter;
}) {
	const currentUser = queriesState.currentUser;
	// Note that this will not work with multiple pages and we will need a state for table principals
	const currentUserIsNotInBaseQuery = !queriesState.contentRestrictions.principals.some(
		({ principal }) => principal.id === currentUser.id,
	);
	// Add the staged principals, one at a time, such that the first will end
	// up on top
	for (const platformPrincipal of [...principalsStagerPrincipals].reverse()) {
		if (platformPrincipal.type === 'email') {
			// Emails don't get added to Specific Access addition
			// They will only be notify if notifications are enabled
			continue;
		}
		const principal = convertUserPickerPrincipal(platformPrincipal);
		const isCurrentUser = principal.id === currentUser.id;
		if (!isCurrentUser) {
			stagePrincipalAddition(setMasterState, principal, addMode);
		} else {
			// If current user is not gaining initial access, don't allow them to demote themselves
			currentUserIsNotInBaseQuery && stagePrincipalAddition(setMasterState, principal, addMode);
		}
	}

	// Remove any staged additions not present in the updated principals list
	filterStagedAdditionsByPrincipals(setMasterState, principalsStagerPrincipals);

	// Update the staged principals list
	setMasterState(
		(s: MasterState): MasterState => ({
			...s,
			principalsStagerPrincipals: principalsStagerPrincipals.map((principal) => {
				if (
					isEmail(principal) &&
					principal.isPendingAction === undefined &&
					fg('twcg-444-invite-usd-improvements-m2-gate')
				) {
					return { ...principal, isPendingAction: true };
				}
				return principal;
			}),
		}),
	);
}

export function getActions(
	controllerProps: ControllerProps,
	controllerExtras: ControllerExtras,
	masterState: MasterState,
	derivedState: DerivedState,
	queriesState: QueriesState,
	setMasterState: MasterStateSetter,
	dispatchCtaFlowEvent: CtaFlowEventDispatcher,
	refetchQueries: RefetchQueries,
): Actions {
	return {
		dismiss() {
			// TODO: if we're in the middle of saving, we should probably ignore this
			// request. Check with designers.

			const { isChanged } = derivedState;
			const { principalsStagerPrincipals } = masterState;

			if (isChanged || principalsStagerPrincipals.length > 0) {
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						isCloseConfirmationOpen: true,
					}),
				);
			} else {
				controllerExtras.teardownController.tearDownAfterPotentialFinalDialogCompletesClose();
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						sessionState: 'exiting',
					}),
				);
			}
		},
		dismissCloseConfirmationDialog() {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					isCloseConfirmationOpen: false,
				}),
			);
		},
		potentialFinalDialogDidCompleteClose() {
			controllerExtras.teardownController.potentialFinalDialogDidCompleteClose();
		},
		closeWithoutSaving() {
			controllerExtras.teardownController.tearDownAfterPotentialFinalDialogCompletesClose();
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					isCloseConfirmationOpen: false,
					sessionState: 'exiting',
					hasAccessRequestsChanges: false,
				}),
			);
		},
		changeGeneralAccessOpenRestricted(becomeRestricted: boolean) {
			const { isUnpublishedDraft } = controllerProps;
			const { stagedGeneralAccessLevel, shouldShowDeletedGroupDetails } = derivedState;
			const { principalsStagerPrincipals } = masterState;

			const openLevelToSet =
				isUnpublishedDraft || shouldShowDeletedGroupDetails ? 'open-can-edit' : 'open-can-view';

			/** This is when the user changes general access to open to "clear" deleted group's restrictions on page.
			 * We need to communicate to the singleContent query that hasRestrictions now needs to be false, we set a value in the master state
			 * called shouldResolveMismatchedRestrictions to true which decides that the unlockIcon now needs to be shown, and fires off updateRestrictions in the
			 * flow controller later on.
			 */
			if (shouldShowDeletedGroupDetails) {
				//check that user picker list is empty, otherwise proceed as usual
				if (principalsStagerPrincipals.length === 0) {
					setMasterState(
						(s: MasterState): MasterState => ({
							...s,
							generalAccessDelta: 'open-can-edit',
							shouldResolveMismatchedRestrictions: true,
						}),
					);
				}
			}

			// If the general access level did not change, don't do anything
			if (
				(becomeRestricted && stagedGeneralAccessLevel === 'restricted') ||
				(!shouldShowDeletedGroupDetails &&
					!becomeRestricted &&
					(stagedGeneralAccessLevel === 'open-can-view' ||
						stagedGeneralAccessLevel === 'open-can-edit'))
			) {
				return;
			}
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					generalAccessDelta: becomeRestricted ? 'restricted' : openLevelToSet,
				}),
			);
		},
		changeGeneralAccessCanViewCanEdit(becomeCanEdit: boolean) {
			const { stagedGeneralAccessLevel } = derivedState;
			// If the general access level did not change, don't do anything
			if (
				(becomeCanEdit && stagedGeneralAccessLevel === 'open-can-edit') ||
				(!becomeCanEdit && stagedGeneralAccessLevel === 'open-can-view')
			) {
				return;
			}
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					generalAccessDelta: becomeCanEdit ? 'open-can-edit' : 'open-can-view',
				}),
			);
		},
		changeStagedPrincipals(principalsStagerPrincipals: UserPickerPrincipal[]) {
			addPrincipals({ principalsStagerPrincipals, queriesState, setMasterState });
		},
		changeAddMode(
			addMode: StagedPrincipalsAddMode,
			principalsStagerPrincipals: UserPickerPrincipal[],
		) {
			addPrincipals({ principalsStagerPrincipals, addMode, queriesState, setMasterState });
		},
		changeSpecificAccessRole(role: Role, id: string) {
			stagePrincipalReassignment(setMasterState, role, id);
		},
		removeSpecificAccessPrincipal(id: string) {
			stagePrincipalRemoval(setMasterState, id);
		},
		removeAllSpecificAccessPrincipals() {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					specificAccessDelta: {
						...s.specificAccessDelta,
						removeAll: true,
						removals: new Set(),
						reassignments: new Map(),
					},
					isSpecificAccessExpanded: false,
				}),
			);
		},
		dismissSpaceAccessDetails() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpaceAccessDetailsOpen: false,
			}));
		},
		openSpaceAccessDetails() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpaceAccessDetailsOpen: true,
			}));
		},
		showOpenAccessWarningDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isOpenAccessWarningDialogOpen: true,
				shouldGeneralAccessSelectMenuBeControlled: true,
			}));
		},
		closeOpenAccessWarningDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isOpenAccessWarningDialogOpen: false,
				shouldGeneralAccessSelectMenuBeControlled: false,
			}));
		},
		openInspectPermsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isInspectPermsDialogOpen: true,
			}));
		},
		dismissInspectPermsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isInspectPermsDialogOpen: false,
			}));
		},
		openPublicLinkDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isPublicLinkDialogOpen: true,
			}));
		},
		closePublicLinkDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isPublicLinkDialogOpen: false,
				lastTriggeredDialogVariant: 'publicLink',
			}));
		},
		openShareToSlackDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isShareToSlackDialogOpen: true,
			}));
		},
		closeShareToSlackDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isShareToSlackDialogOpen: false,
				lastTriggeredDialogVariant: 'shareToSlack',
			}));
		},
		openAccessRequestsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isAccessRequestsDialogOpen: true,
			}));
		},
		closeAccessRequestsDialog() {
			const { hasAccessRequestsChanges } = masterState;

			// Check if there are unsaved changes in the access requests dialog
			if (hasAccessRequestsChanges) {
				setMasterState(
					(s: MasterState): MasterState => ({
						...s,
						isCloseConfirmationOpen: true,
					}),
				);
			} else {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsDialogOpen: false,
					lastTriggeredDialogVariant: 'accessRequests',
				}));

				void refetchQueries();
			}
		},
		clearElementToFocus() {
			setMasterState((s: MasterState) => ({
				...s,
				lastTriggeredDialogVariant: null,
			}));
		},
		showFlag(flag) {
			return controllerExtras.showFlag(flag);
		},
		showErrorFlag(flag) {
			return controllerExtras.showErrorFlag(flag);
		},
		handleCtaClick() {
			const { contentId, contentType, contentSubType, isUnpublishedDraft } = controllerProps;
			const { numberOfAncestors, userCanEditRestrictions, currentUser, edition } = queriesState;
			const { principalsStagerPrincipals, optionalMessage, shouldResolveMismatchedRestrictions } =
				masterState;
			const {
				isChanged,
				stagedGeneralAccessLevel,
				natureOfCta,
				stagedNotificationRecipients,
				isPublishModalFlow,
			} = derivedState;
			const { addMode } = masterState;
			const {
				mutateContentRestrictions,
				exportPublishModalRestrictions,
				sendNotification,
				successFlag,
				errorFlag,
				fireAnalyticEvent,
				teardownController: { tearDownAfterPotentialFinalDialogCompletesClose },
				experienceTracker,
				callResolveMismatchedRestrictions,
			} = controllerExtras;

			const beginTeardown = () => {
				// Tell the teardown manager to unmount the controller after the dialog
				// closes
				tearDownAfterPotentialFinalDialogCompletesClose();

				// Switch the active dialog to an empty one, to close it. Its
				// `onCloseComplete` will trigger the unmounting
				setMasterState((s2: MasterState) => ({
					...s2,
					sessionState: 'exiting',
					hasAccessRequestsChanges: false,
					// Deliberately don't set `isSaving: false` here. If we did,
					// the elements would regain their interactivity, meaning:
					// 1) the dialog will pop during its exit animation
					// 2) what if somebody then clicked one of those controls?
				}));
			};

			// We need to ensure we retrieve the most updated principal map to use during a CTA click.
			const updatedPrincipalMap = makePrincipalsMap(masterState, queriesState, {
				isCtaClick: true,
				stagedGeneralAccessLevel,
			});

			if (isPublishModalFlow) {
				exportPublishModalRestrictions(stagedGeneralAccessLevel, updatedPrincipalMap);
				beginTeardown();
			} else {
				dispatchCtaFlowEvent({
					contentId,
					edition,
					numberOfAncestors,
					type: 'cta',
					addMode,
					mutateContentRestrictions,
					currentUser,
					sendNotification,
					successFlag,
					errorFlag,
					beginTeardown,
					isMutatingAction: isChanged,
					isNotifyingAction: natureOfCta === 'share',
					userCanEditRestrictions,
					stagedGeneralAccessLevel,
					principalsMap: updatedPrincipalMap,
					optionalMessage,
					principalsStagerPrincipals,
					stagedNotificationRecipients,
					validateRestrictedPrincipals,
					experienceTracker,
					fireAnalyticEvent,
					contentSubType,
					contentType,
					callResolveMismatchedRestrictions,
					isResolvingAction: shouldResolveMismatchedRestrictions,
					isUnpublishedDraft,
				});
			}
		},
		changeOptionalMessage(message: string) {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					optionalMessage: message,
				}),
			);
		},
		setNotifyChecked(newValue: boolean) {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					notifyCheckedIfAvailable: newValue,
					// If we're unchecking the box, then if we check it again we want the
					// 'Add message' textarea to not show by default
					showMessageComposerUnderCheckbox: newValue ? s.showMessageComposerUnderCheckbox : false,
				}),
			);
		},
		toggleNotifyAddMessage() {
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					showMessageComposerUnderCheckbox: !s.showMessageComposerUnderCheckbox,
				}),
			);
		},
		doNotifyGroupsConfirmed() {
			dispatchCtaFlowEvent({ type: 'doNotifyGroups' });
		},
		doNotNotifyGroupsConfirmed() {
			dispatchCtaFlowEvent({ type: 'doNotNotifyGroups' });
		},
		// Defaulting isGuestError to false as this is an edge case that is being handled.
		// A better solution for handling guest errors will be implemented in the future.
		// https://product-fabric.atlassian.net/browse/USD-633
		ignoreRestrictionsAndContinue(isGuestError = false) {
			dispatchCtaFlowEvent({ type: 'ignoreRestrictions', shouldSkipFlagRaise: isGuestError });
		},
		resolveRestrictionsComplete() {
			dispatchCtaFlowEvent({ type: 'resolveRestrictionsComplete' });
		},
		toggleSpecificAccessExpanded() {
			setMasterState((s: MasterState) => ({
				...s,
				isSpecificAccessExpanded: !s.isSpecificAccessExpanded,
			}));
		},
		closeAccessRequestsResolveRestrictionsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isAccessRequestsResolveRestrictionsDialogOpen: false,
				accessRequestsCurrentUserAddMode: undefined,
			}));
		},
		closeAccessRequestsWarnRestrictionsDialog() {
			setMasterState((s: MasterState) => ({
				...s,
				isAccessRequestsWarnRestrictionsDialogOpen: false,
			}));
		},
		async maybeStartBlockedAccessFlowForUser({
			id,
			avatarUrl,
			name,
			email,
			sitePermissionType,
			accessType,
		}) {
			const { contentId } = controllerProps;
			const { numberOfAncestors, edition } = queriesState;

			const { fireAnalyticEvent, cloudId } = controllerExtras;
			const addMode: MasterState['accessRequestsCurrentUserAddMode'] =
				accessType === 'EDIT' ? 'as-editor' : 'as-viewer';
			const userPrincipal: UserPrincipal = {
				id,
				avatarUrl,
				name,
				email,
				sitePermissionType,
				knownErrors: undefined,
			};

			const { showWarnRestrictionsDialog, showResolveRestrictionsDialog } =
				await validateRestrictedPrincipals({
					contentId,
					cloudId,
					edition,
					numberOfAncestors,
					addMode,
					stagedUserPrincipals: [userPrincipal],
					setMasterState,
					fireAnalyticEvent,
				});

			if (showWarnRestrictionsDialog) {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsWarnRestrictionsDialogOpen: true,
				}));
			}

			if (showResolveRestrictionsDialog) {
				setMasterState((s: MasterState) => ({
					...s,
					isAccessRequestsResolveRestrictionsDialogOpen: true,
					accessRequestsCurrentUserAddMode: addMode,
				}));
			}

			return {
				isBlockedAccessFlowStarted: showWarnRestrictionsDialog || showResolveRestrictionsDialog,
			};
		},
		setAccessRequestsChanges(hasChanges: boolean) {
			setMasterState((s: MasterState) => ({
				...s,
				hasAccessRequestsChanges: hasChanges,
			}));
		},
		markPrincipalsAsSkipped(skippedPrincipals: string[]) {
			const { principalsStagerPrincipals } = masterState;
			setMasterState((s: MasterState) => ({
				...s,
				principalsStagerPrincipals: principalsStagerPrincipals.map((principal) => {
					if (isEmail(principal) && skippedPrincipals.includes(principal.id)) {
						return { ...principal, isPendingAction: false };
					}
					return principal;
				}),
			}));
		},
		markPrincipalsAsInvited(
			invitedPrincipals: { id: string; email: string }[],
			errorPrincipals: { email: string }[],
		) {
			const { principalsStagerPrincipals } = masterState;
			const principalsToAdd: (Principal & UserPrincipal)[] = invitedPrincipals.map(
				({ email, id }) => ({
					type: 'user' as const,
					id,
					email,
					name: email,
					avatarUrl: '',
					knownErrors: { accessWarning: 'pending-site-access-warning', parentLink: null },
					sitePermissionType: 'none',
				}),
			);
			principalsToAdd.forEach((principal) => {
				stagePrincipalAddition(setMasterState, principal);
			});
			setMasterState((s: MasterState) => ({
				...s,
				principalsStagerPrincipals: principalsStagerPrincipals.map((principal) => {
					const invitedPrincipal = principalsToAdd.find(({ email }) => email === principal.id);
					if (isEmail(principal) && invitedPrincipal) {
						const { type, id, email, name } = invitedPrincipal;
						return { type, id, email, name };
					}
					const errorPrincipal = errorPrincipals.find(({ email }) => email === principal.id);
					if (isEmail(principal) && errorPrincipal) {
						return { ...principal, isPendingAction: false };
					}
					return principal;
				}),
			}));
		},
	};
}
